import { Loading3QuartersOutlined } from "@ant-design/icons";
import { Button, ConfigProvider, Flex, Space, Spin, Typography } from "antd";
import type { ButtonProps } from "antd";
import { withClickTracking } from "hooks/useAnalytics";
import { AnalyticsEventsMap } from "types/analytics";
import { useAntTheme } from "hooks/useAntTheme";
import { ReactNode } from "react";
import styled from "styled-components";
import { Checked } from "../Checked/Checked";
import { radius } from "@libertify/ui";

export type FatButtonProps = {
  title: string;
  arrow?: boolean;
  checked?: boolean;
  type?: "default" | "primary";
  icons?: ReactNode[];
  titleFontSize?: number;
  contentFontSize?: number;
  titleFontWeight?: number;
  contentFontWeight?: number;
  iconSize?: number;
  bottomItem?: ReactNode;
  bottomLabel?: string;
  content?: ReactNode;
  iconColor?: string;
  disabled?: boolean;
  checkedSize?: number;
} & Omit<ButtonProps, "content">;

export const FatButton = ({
  icon,
  block,
  title,
  loading,
  content,
  arrow,
  type = "default",
  style,
  checked,
  titleFontSize,
  contentFontSize,
  titleFontWeight = 400,
  contentFontWeight = 400,
  size = "middle",
  iconSize,
  bottomItem,
  bottomLabel,
  iconColor,
  disabled,
  checkedSize = 40,
  ...rest
}: FatButtonProps) => {
  const { theme } = useAntTheme();
  const icons = rest.icons || [icon];
  const fontSize = {
    title: { large: 42, middle: 24, small: 20 },
    content: { large: 28, middle: 18, small: 12 },
  };
  iconSize = iconSize ?? 55;
  const getIconColor = () => {
    if (iconColor) {
      return iconColor;
    } else {
      if (type === "primary") {
        return "rgba(255, 255, 255, 0.5)";
      } else {
        return theme.colorPrimary;
      }
    }
  };
  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            colorText:
              type === "primary" ? undefined : theme.colorTextDescription,
            defaultHoverBg: theme.colorBorderSecondary,
            defaultHoverColor: theme.colorTextDescription,
          },
        },
      }}
    >
      <Flex
        vertical
        data-testid="fat-button"
        justify="center"
        align="center"
        style={{
          position: "relative",
          width: style?.width,
          flex: style?.flex,
        }}
      >
        {checked && <Checked checkedSize={checkedSize} />}
        <StyledMenuButton
          disabled={disabled}
          {...rest}
          style={{
            ...(block ? { width: "100%" } : null),
            ...style,
            boxShadow: `0 0 10px ${theme.colorBorder}`,
          }}
          type={type}
          data-testid={block ? "fat-button-block" : "fat-button"}
        >
          <Flex
            justify="center"
            align="center"
            style={{
              width: "100%",
            }}
          >
            <Flex vertical justify="center" align="center">
              <Flex gap={10}>
                {icons.map((icon, index) => {
                  return (
                    <Flex
                      key={index}
                      align="center"
                      justify="center"
                      style={{
                        width: iconSize,
                        height: iconSize,
                        borderRadius: Math.floor(iconSize / 2.8),
                        backgroundColor: getIconColor(),
                        color: theme.colorBgElevated,
                        marginBottom: size === "small" ? 10 : 15,
                      }}
                    >
                      {icon}
                    </Flex>
                  );
                })}
              </Flex>
              {/* <Flex
                  align="center"
                  justify="center"
                  style={{
                    width: 55,
                    height: 55,
                    borderRadius: 20,
                    backgroundColor:
                      type === "primary"
                        ? "rgba(255, 255, 255, 0.5)"
                        : theme.colorPrimary,
                    color: theme.colorBgElevated,
                    marginBottom: 15,
                  }}
                >
                  {icon}
                </Flex> */}

              <Flex vertical align="center" gap={2}>
                <span
                  style={{
                    fontSize: titleFontSize ?? fontSize.title[size],
                    whiteSpace: "normal",
                    lineHeight: 1,
                    fontWeight: titleFontWeight,
                    letterSpacing: "-0.04em",
                  }}
                >
                  {title}
                </span>
                <Space size="small" align="center">
                  {loading && (
                    <Spin
                      indicator={<Loading3QuartersOutlined spin />}
                      size="small"
                      style={{ color: theme.colorTextDescription }}
                    />
                  )}
                  <Flex
                    gap={-10}
                    data-testid="fat-button-content"
                    style={{
                      fontSize: contentFontSize ?? fontSize.content[size],
                      whiteSpace: "normal",
                      marginTop: 2,
                      color:
                        type === "primary" ? theme.colorBgElevated : undefined,
                      letterSpacing: "-0.04em",
                      lineHeight: 1.1,
                      fontWeight: contentFontWeight,
                    }}
                  >
                    {content}
                  </Flex>
                </Space>
              </Flex>

              {(bottomItem || bottomLabel) && (
                <Space>
                  {bottomLabel && (
                    <Typography.Text
                      data-testid="fat-button-bottom-label"
                      style={{
                        fontSize: 12,
                        color: checked ? theme.colorBgElevated : undefined,
                      }}
                    >
                      {bottomLabel}
                    </Typography.Text>
                  )}
                  {bottomItem}
                </Space>
              )}
            </Flex>
          </Flex>
        </StyledMenuButton>
      </Flex>
    </ConfigProvider>
  );
};

const StyledMenuButton = styled(Button)`
  height: auto;
  padding: 8px 16px;
  border-radius: ${radius.box}px;
  margin-bottom: 10px;
  border: none;
`;

export const TrackedFatButton = withClickTracking(FatButton) as <
  T extends keyof AnalyticsEventsMap,
>(
  props: FatButtonProps & {
    trackingEventName?: T;
    trackingProps?: AnalyticsEventsMap[T];
    trackingName?: string;
    trackingText?: string;
  }
) => JSX.Element;
